/** Browser speech-to-text helpers for the AI chat composer (BR-11 voice channel). */

export type SpeechRecognitionLike = {
  continuous: boolean
  interimResults: boolean
  lang: string
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}

export type SpeechRecognitionResultEventLike = {
  resultIndex: number
  results: ArrayLike<{
    isFinal: boolean
    0: { transcript: string }
  }>
}

export type SpeechRecognitionErrorEventLike = {
  error: string
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike

/** Join dictated text onto an existing composer value without double spaces. */
export function appendDictation(base: string, chunk: string, maxLength: number): string {
  const piece = chunk.trim()
  if (!piece) return base.slice(0, maxLength)
  const joined = base.trim() ? `${base.trimEnd()} ${piece}` : piece
  return joined.slice(0, maxLength)
}

export function getSpeechRecognitionCtor(
  globalObj: typeof globalThis = globalThis,
): SpeechRecognitionCtor | null {
  const g = globalObj as typeof globalThis & {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return g.SpeechRecognition ?? g.webkitSpeechRecognition ?? null
}

export function speechRecognitionSupported(globalObj: typeof globalThis = globalThis): boolean {
  return getSpeechRecognitionCtor(globalObj) != null
}

/** User-facing copy for Web Speech API error codes. */
export function speechErrorMessage(code: string): string | null {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone permission is blocked. Allow the mic for this site and try again.'
    case 'audio-capture':
      return 'No microphone was found. Check your input device and try again.'
    case 'network':
      return 'Speech recognition could not reach the browser speech service (network or CSP).'
    case 'no-speech':
      return null
    case 'aborted':
      return null
    default:
      return 'Speech recognition stopped unexpectedly. Try the mic again.'
  }
}
